<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { UiDialog, UiInputField, UiSelect, UiStandardButton, UiText } from '@shopexperts/shared';
import type { PageSchema } from '@shopexperts/shared';
import { usePageSchemas } from '../composables/usePageSchemas';
import { useDeleteSchema } from '../composables/useDeleteSchema';
import { useCreateSchemaFlow } from '../composables/useCreateSchemaFlow';
import { isSchemaSort, sortSchemas, type SchemaSort } from '../utils/schemaListSort';
import CreateSchemaDialog from '../components/schema-builder/CreateSchemaDialog.vue';
import SchemaListRow from '../components/schema-list/SchemaListRow.vue';

const router = useRouter();
const { schemas, isLoading, error } = usePageSchemas();
const { mutateAsync: deleteSchema, isPending: isDeleting } = useDeleteSchema();
const createFlow = useCreateSchemaFlow();

const search = ref('');
const sort = ref<SchemaSort>('label');
const schemaToDelete = ref<PageSchema | null>(null);

const sortOptions = [
  { label: 'Label (A–Z)', value: 'label' },
  { label: 'Most pages', value: 'pages' },
  { label: 'Recently updated', value: 'updated' },
];

const visibleSchemas = computed(() => sortSchemas(schemas.value, search.value, sort.value));
const hasSchemas = computed(() => schemas.value.length > 0);

function onSortChange(value: string | string[]) {
  const next = Array.isArray(value) ? value[0] : value;
  if (isSchemaSort(next)) sort.value = next;
}

function openSchemaBuilder(schema: PageSchema) {
  router.push({ name: 'admin-page-schema-builder', params: { schemaId: schema.id } });
}

async function submitCreateSchema() {
  const createdId = await createFlow.submit();
  if (createdId) router.push({ name: 'admin-page-schema-builder', params: { schemaId: createdId } });
}

async function confirmDelete() {
  if (!schemaToDelete.value) return;
  try {
    await deleteSchema(schemaToDelete.value.id);
    schemaToDelete.value = null;
  } catch {
    // Reported by the mutation's own error toast; keep the dialog open to retry.
  }
}
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-secondary p-8 font-light">
    <div class="mx-auto flex max-w-5xl flex-col gap-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <UiText as="h1" variant="h3" text="Pages" />
          <UiText
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="Pick a schema to edit its pages, or open the schema itself to change its fields."
          />
        </div>
        <UiStandardButton variant="primary" label="New schema" icon="plus-square" @click="createFlow.openDialog()" />
      </div>

      <div v-if="hasSchemas" class="flex flex-wrap items-center gap-3">
        <div class="min-w-56 flex-1">
          <UiInputField v-model="search" icon="search-md" placeholder="Search schemas..." />
        </div>
        <div class="w-56">
          <UiSelect :model-value="sort" :options="sortOptions" @update:model-value="onSortChange" />
        </div>
      </div>

      <UiText v-if="isLoading" as="p" variant="p" class="py-8 text-center text-se-gray-700" text="Loading schemas..." />

      <UiText
        v-else-if="error"
        as="p"
        variant="p"
        class="py-8 text-center text-red-500"
        :text="`Failed to load schemas: ${error.message}`"
      />

      <div v-else-if="!hasSchemas" class="flex flex-col items-center gap-4 py-16 text-center">
        <div>
          <UiText as="h2" variant="h6" class="font-semibold" text="No page schemas yet" />
          <UiText
            as="p"
            variant="p-small"
            class="mt-1 text-se-gray-700"
            text="A schema defines the fields its pages can fill in. Create one to start building pages."
          />
        </div>
        <UiStandardButton variant="primary" label="New schema" icon="plus-square" @click="createFlow.openDialog()" />
      </div>

      <div v-else-if="!visibleSchemas.length" class="py-16 text-center">
        <UiText as="p" variant="p-small" class="text-se-gray-700" :text="`No schemas match “${search}”.`" />
      </div>

      <div v-else class="flex flex-col gap-3">
        <SchemaListRow
          v-for="schema in visibleSchemas"
          :key="schema.id"
          :schema="schema"
          @edit-schema="openSchemaBuilder(schema)"
          @delete-schema="schemaToDelete = schema"
        />
      </div>
    </div>

    <CreateSchemaDialog
      v-model="createFlow.open.value"
      :name="createFlow.name.value"
      :label="createFlow.label.value"
      :description="createFlow.description.value"
      :can-create="createFlow.canCreate.value"
      :is-creating="createFlow.isCreating.value"
      @update-name="createFlow.updateName"
      @update-label="createFlow.updateLabel"
      @update-description="createFlow.updateDescription"
      @submit="submitCreateSchema"
    />

    <UiDialog
      :model-value="Boolean(schemaToDelete)"
      title="Delete schema"
      :text="`Delete the schema '${schemaToDelete?.label}' and stop serving its ${schemaToDelete?.pages_count ?? 0} page(s)? This action cannot be undone.`"
      primary-action-label="Delete"
      secondary-action-label="Cancel"
      :primary-action-loading="isDeleting"
      @update:model-value="(open: boolean) => { if (!open) schemaToDelete = null }"
      @primary-action="confirmDelete"
      @secondary-action="schemaToDelete = null"
    />
  </main>
</template>
